<template>
  <PriceCompareLayout>
    <BreadcrumbBar :breadcrumbs="breadcrumbs" />
    <div class="container-fluid py-5">
      <div class="row">
        <!-- Main content column (66%) -->
        <div class="col-lg-9">
          <!-- Hero section similar to Articles page -->
          <div class="hero-card mb-5">
            <div class="hero-content p-4 p-md-5">
              <div class="hero-decoration"></div>
              <div class="row">
                <div class="col-md-3 col-lg-2 mb-4 mb-md-0">
                  <div class="bg-white p-3 rounded-lg shadow-sm d-flex justify-content-center align-items-center">
                    <img 
                      v-if="retailer.logo" 
                      :src="`/retailer-images${retailer.logo}`"
                      :alt="retailer.name + ' logo'" 
                      class="img-fluid"
                      style="max-height: 80px; max-width: 100%;"
                    />
                    <div v-else class="display-4 text-muted fw-bold">{{ retailer.name.charAt(0) }}</div>
                  </div>
                </div>
                <div class="col-md-9 col-lg-10">
                  <h1 class="display-5 fw-bold mb-2">{{ retailer.name }}</h1>
                  <div v-if="retailer.group" class="mb-2">
                    <span class="badge bg-primary">{{ retailer.group.name }}</span>
                  </div>
                  <div class="d-flex align-items-center mt-2">
                    <div class="me-4 d-flex align-items-center">
                      <!-- <i class="bi bi-star-fill text-warning me-1"></i>
                      <span class="fw-bold">4.8</span>
                      <span class="text-muted ms-1">(1373 anmeldelser)</span> -->
                    </div>
                    <a v-if="retailer.website" :href="retailer.website" target="_blank" rel="noopener noreferrer" 
                       class="btn btn-sm btn-pricecompare-secondary d-flex align-items-center">
                      <i class="bi bi-box-arrow-up-right me-1"></i>
                      <span>Besøg butik</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- About section with featured style -->
          <div class="card mb-5 border-0 bg-light">
            <div class="row g-0">
              <div class="col-md-8">
                <div class="p-4 p-md-5">
                  <div class="d-flex mb-3">
                    <span class="badge bg-pricecompare-secondary me-2">Om forhandleren</span>
                  </div>
                  <h2 class="card-title h3 mb-3">Om {{ retailer.name }}</h2>
                  <p class="card-text mb-4" v-if="retailer.description">
                    {{ retailer.description }}
                  </p>
                  <p class="card-text mb-4 text-muted fst-italic" v-else>
                    Der er endnu ikke tilføjet en beskrivelse for denne forhandler.
                  </p>
                </div>
              </div>
              <div class="col-md-4 bg-white d-flex align-items-center justify-content-center">
                <div class="p-4">
                  <div class="row g-3">
                    <div v-if="retailer.website" class="col-12">
                      <div class="d-flex align-items-start">
                        <i class="bi bi-globe2 text-pricecompare-secondary fs-5 me-2"></i>
                        <div>
                          <h6 class="mb-0 text-muted">Hjemmeside</h6>
                          <a :href="retailer.website" target="_blank" rel="noopener noreferrer" class="text-decoration-none">
                            {{ formatURL(retailer.website) }}
                          </a>
                        </div>
                      </div>
                    </div>
                    
                    <div v-if="retailer.email" class="col-12">
                      <div class="d-flex align-items-start">
                        <i class="bi bi-envelope text-pricecompare-secondary fs-5 me-2"></i>
                        <div>
                          <h6 class="mb-0 text-muted">Email</h6>
                          <a :href="`mailto:${retailer.email}`" class="text-decoration-none">{{ retailer.email }}</a>
                        </div>
                      </div>
                    </div>
                    
                    <div v-if="retailer.phone" class="col-12">
                      <div class="d-flex align-items-start">
                        <i class="bi bi-telephone text-pricecompare-secondary fs-5 me-2"></i>
                        <div>
                          <h6 class="mb-0 text-muted">Telefon</h6>
                          <a :href="`tel:${retailer.phone}`" class="text-decoration-none">{{ retailer.phone }}</a>
                        </div>
                      </div>
                    </div>
                    
                    <div v-if="retailer.founded_year" class="col-12">
                      <div class="d-flex align-items-start">
                        <i class="bi bi-calendar-event text-pricecompare-secondary fs-5 me-2"></i>
                        <div>
                          <h6 class="mb-0 text-muted">Grundlagt</h6>
                          <p class="mb-0">{{ retailer.founded_year }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Address with map visualization -->
          <div v-if="retailer.address" class="card mb-5 border-0 shadow-sm">
            <div class="card-body p-0">
              <div class="row g-0">
                <div class="col-md-5 p-4">
                  <h3 class="h4 mb-3">
                    <i class="bi bi-geo-alt text-pricecompare-secondary me-2"></i>Adresse
                  </h3>
                  <p>{{ retailer.address }}</p>
                  <a href="#" class="btn btn-outline-secondary btn-sm">
                    <i class="bi bi-map me-2"></i>Vis på kort
                  </a>
                </div>
                <div class="col-md-7 bg-light">
                  <div style="height: 200px;" class="d-flex align-items-center justify-content-center bg-secondary">
                    <i class="bi bi-map text-white" style="font-size: 3rem;"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Popular Products Carousel -->
          <div class="card border-0 shadow-sm mb-5">
            <div class="card-body">
              <ProductCarousel 
                headline="Populære Produkter"
                tagline="Produkter hvor denne forhandler har den bedste pris"
                :products="popularProducts"
                :productsPrPage="4"
                :showMoreLinkAnchor="'/products?retailer=' + retailer.id"
                :componentIdentifier="'popularProductsCarousel'"
              />
            </div>
          </div>
          
          <!-- Categories in a visual grid -->
          <div class="card mb-5 border-0 shadow-sm" v-if="retailerCategories && retailerCategories.length > 0">
            <div class="card-body">
              <h3 class="h4 mb-4">
                <i class="bi bi-folder text-pricecompare-secondary me-2"></i>Produktkategorier
              </h3>
              <div class="row g-3">
                <div v-for="category in retailerCategories" :key="category.id" class="col-md-4 col-lg-3">
                  <a :href="category.url" class="text-decoration-none">
                    <div class="card h-100 border-0 shadow-sm hover-card">
                      <div class="card-body text-center py-4">
                        <i class="bi bi-tag text-secondary mb-2" style="font-size: 2rem;"></i>
                        <h5 class="card-title">{{ category.name }}</h5>
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Sidebar column (34%) -->
        <div class="col-lg-3">
          <!-- Payment Options Card -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-body p-4">
              <h4 class="card-title">
                <i class="bi bi-credit-card text-pricecompare-secondary me-2"></i>Betalingsmuligheder
              </h4>
              <hr class="my-3">
              <div v-if="retailer.paymentOptions && retailer.paymentOptions.length > 0" class="d-flex flex-wrap gap-2">
                <div v-for="payment in retailer.paymentOptions" :key="payment.id" 
                     class="bg-light rounded p-2 d-flex align-items-center justify-content-center">
                  <span v-if="payment.logo" class="payment-icon">
                    <img :src="payment.logo" :alt="payment.name" class="h-6">
                  </span>
                  <span v-else class="small">{{ payment.name }}</span>
                </div>
              </div>
              <p v-else class="text-muted fst-italic mb-0">Ingen betalingsmuligheder oplyst.</p>
            </div>
          </div>
          
          <!-- Carrier Options Card -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-body p-4">
              <h4 class="card-title">
                <i class="bi bi-truck text-pricecompare-secondary me-2"></i>Forsendelsesmuligheder
              </h4>
              <hr class="my-3">
              <div v-if="retailer.carrierOptions && retailer.carrierOptions.length > 0" class="d-flex flex-wrap gap-2">
                <div v-for="carrier in retailer.carrierOptions" :key="carrier.id" 
                     class="bg-light rounded p-2 d-flex align-items-center justify-content-center">
                  <span v-if="carrier.logo" class="carrier-icon">
                    <img :src="carrier.logo" :alt="carrier.name" class="h-6">
                  </span>
                  <span v-else class="small">{{ carrier.name }}</span>
                </div>
              </div>
              <p v-else class="text-muted fst-italic mb-0">Ingen forsendelsesmuligheder oplyst.</p>
            </div>
          </div>
          
          <!-- Trust and Certifications Card -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-body p-4">
              <h4 class="card-title">
                <i class="bi bi-shield-check text-pricecompare-secondary me-2"></i>Certificeringer
              </h4>
              <hr class="my-3">
              <div v-if="retailer.certifications && retailer.certifications.length > 0" class="d-flex flex-wrap gap-2">
                <div v-for="cert in retailer.certifications" :key="cert.id" 
                     class="bg-light rounded p-2">
                  <img :src="cert.logo" :alt="cert.name" class="h-8">
                </div>
              </div>
              <div v-else class="text-center py-3">
                <i class="bi bi-shield-fill-check text-muted" style="font-size: 3rem;"></i>
                <p class="text-muted fst-italic mb-0 mt-2">Ingen certificeringer registreret</p>
              </div>
            </div>
          </div>
          
          <!-- Banner placeholder -->
          <div class="card border-0 bg-light mb-4">
            <div class="card-body p-4 text-center">
              <h5 class="mb-3">Tilbud fra {{ retailer.name }}</h5>
              <div class="bg-secondary p-5 d-flex align-items-center justify-content-center">
                <p class="text-white m-0">300 x 250</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PriceCompareLayout>
</template>

<script setup>
import { Head } from '@inertiajs/vue3';
import PriceCompareLayout from '@/Layouts/PriceCompareLayout.vue';
import ProductCarousel from '@/Components/Carousels/ProductCarousel.vue';
import BreadcrumbBar from '@/Components/BreadcrumbBar.vue';
import { ref, computed } from 'vue';

const props = defineProps({
  breadcrumbs: {
    type: Array,
    default: () => []
  },
  retailer: Object,
  popularProducts: {
    type: Array,
    default: () => []
  },
  retailerCategories: {
    type: Array,
    default: () => []
  }
});

// Format URL to make it more readable
const formatURL = (url) => {
  if (!url) return '';
  return url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
};
</script>

<style scoped>
/* Pricecompare brand colors */
.btn-pricecompare-secondary {
  background-color: #FF5733;
  border-color: #FF5733;
  color: white;
}

.btn-pricecompare-secondary:hover {
  background-color: #E64A19;
  border-color: #E64A19;
  color: white;
}

.text-pricecompare-secondary {
  color: #FF5733;
}

.bg-pricecompare-secondary {
  background-color: #FF5733;
}

/* Hero styling similar to Articles page */
.hero-card {
  position: relative;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border-left: 4px solid #FF5733;
}

.hero-content {
  position: relative;
  z-index: 2;
}

.hero-decoration {
  position: absolute;
  top: 0;
  right: 0;
  width: 150px;
  height: 150px;
  background: radial-gradient(circle at top right, rgba(255, 87, 51, 0.15) 0%, rgba(255, 87, 51, 0) 70%);
  z-index: 1;
}

/* Card hover effects */
.hover-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.hover-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .hero-card h1 {
    font-size: 2rem;
  }
}
</style>
